import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface SSRConfig {
  symbol: 'BTC' | 'ETH';
  ssrLength: number;
  bbLength: number;
  bbUpperMult: number;
  bbLowerMult: number;
  bbMaType: 'SMA' | 'EMA' | 'RMA' | 'WMA';
  period: '1Y' | '2Y' | '3Y' | '5Y';
  rsiLength: number;
  rsiOverbought: number;
  rsiOversold: number;
}

export const DEFAULT_SSR_CONFIG: SSRConfig = {
  symbol: 'BTC',
  ssrLength: 200,
  bbLength: 200,
  bbUpperMult: 2.0,
  bbLowerMult: 1.8, // Asymmetric as per TradingView config
  bbMaType: 'SMA',
  period: '2Y',
  rsiLength: 14,
  rsiOverbought: 75,
  rsiOversold: 35
};

export interface SSRCurrentData {
  ssr: number;
  ratio: number;
  upper: number | null;
  lower: number | null;
  basis: number | null;
  ssrRsi: number | null;
  signal: 'overbought' | 'oversold' | 'neutral';
  signalSource: 'bb' | 'rsi';
  btcMarketCap: number;
  stablecoinSupply: number;
  btcPrice: number;
  rsiOverbought: number;
  rsiOversold: number;
}

export interface SSRHistoryPoint {
  date: number;
  ssr: number;
  ratio: number;
  upper: number | null;
  lower: number | null;
  basis: number | null;
  ssrRsi: number | null;
  btcPrice: number;
  stablecoinSupply: number;
}

export interface SSRAlphaData {
  current: SSRCurrentData;
  history: SSRHistoryPoint[];
  hasEnoughForBB: boolean;
  config: SSRConfig;
}

export function useSSRAlpha(config: Partial<SSRConfig> = {}) {
  const mergedConfig = { ...DEFAULT_SSR_CONFIG, ...config };
  
  return useQuery({
    queryKey: ['ssr-alpha', mergedConfig],
    queryFn: async (): Promise<SSRAlphaData> => {
      const { data, error } = await supabase.functions.invoke('ssr-alpha-signal', {
        body: mergedConfig
      });

      if (error) throw error;
      return data;
    },
    staleTime: 30 * 60 * 1000, // 30 minutes
    refetchInterval: 60 * 60 * 1000, // 1 hour
  });
}

export function getSignalColor(signal: 'overbought' | 'oversold' | 'neutral'): string {
  switch (signal) {
    case 'overbought':
      return 'text-red-500';
    case 'oversold':
      return 'text-emerald-500';
    default:
      return 'text-muted-foreground';
  }
}

export function getSignalBgColor(signal: 'overbought' | 'oversold' | 'neutral'): string {
  switch (signal) {
    case 'overbought':
      return 'bg-red-500/10';
    case 'oversold':
      return 'bg-emerald-500/10';
    default:
      return 'bg-muted/50';
  }
}

export function getSignalLabel(signal: 'overbought' | 'oversold' | 'neutral'): string {
  switch (signal) {
    case 'overbought':
      return 'SOBRECOMPRADO';
    case 'oversold':
      return 'SOBREVENDIDO';
    default:
      return 'NEUTRO';
  }
}

export function getSignalInterpretation(signal: 'overbought' | 'oversold' | 'neutral'): string {
  switch (signal) {
    case 'overbought':
      return 'BTC está relativamente caro vs liquidez em stablecoins. Cautela para novas compras.';
    case 'oversold':
      return 'BTC está relativamente barato vs liquidez disponível. Oportunidade de acumulação.';
    default:
      return 'Valuation do BTC está em linha com a liquidez disponível no mercado.';
  }
}

export function getRsiZoneColor(rsi: number | null, overbought: number, oversold: number): string {
  if (rsi === null) return 'text-muted-foreground';
  if (rsi >= overbought) return 'text-red-500';
  if (rsi <= oversold) return 'text-emerald-500';
  return 'text-amber-500';
}
